import { ROUTES } from "@/constants/routes";
import { isPostPublished } from "./utils";

import type { RoutePath } from "@/constants/routes";

export enum BlogCategory {
  Security = "Security",
  LaunchIntegrity = "Launch Integrity",
  Tokenomics = "Tokenomics",
  Operations = "Operations",
  Compliance = "Compliance",
  MultichainOFT = "Multichain/OFT",
  Governance = "Governance",
  Identity = "Identity",
  UXSecurity = "UX/Security",
  UserSecurity = "User Security",
  PrivacySecurity = "Privacy/Security",
  Privacy = "Privacy",
  Architecture = "Architecture",
  Launchpad = "Launchpad",
  Platform = "Platform",
}

export interface BlogPostMeta {
  route: RoutePath;
  title: string;
  description: string;
  category: BlogCategory;
  tags: string[];
  publishedDate: string; // ISO 8601 format: YYYY-MM-DD
  lastUpdated: string; // ISO 8601 format: YYYY-MM-DD
  author: string;
}

export const BLOG_POSTS: BlogPostMeta[] = [
  {
    route: ROUTES.BLOG_NON_CASHABLE_REWARDS,
    title: "Why Non-Cashable Rewards Matter for Compliance-Forward Token Design",
    description:
      "How non-cashable rewards support compliance-forward token design by reducing sell-pressure incentives, filtering toward aligned users, making measurement honest, and making the system defensible under scrutiny.",
    category: BlogCategory.Tokenomics,
    tags: [
      "non-cashable-rewards",
      "compliance-forward",
      "token-design",
      "reward-design",
      "token-incentives",
      "utility-bound-rewards",
      "compliance",
      "token-economics",
      "reward-systems",
      "governance",
    ],
    publishedDate: "2026-02-06",
    lastUpdated: "2026-02-06",
    author: "Jeremy R DeYoung",
  },
  {
    route: ROUTES.BLOG_SINGLE_SIDED_VS_LP_STAKING,
    title: "Single-Sided vs LP Staking: Stability, Liquidity Support, and User Tradeoffs",
    description:
      "How single-sided staking and LP staking serve different economic jobs—single-sided for stability and alignment, LP staking for liquidity and market quality—and why understanding the tradeoffs matters for building credible token economics.",
    category: BlogCategory.Tokenomics,
    tags: [
      "staking",
      "single-sided-staking",
      "LP-staking",
      "liquidity-provider-staking",
      "staking-models",
      "token-staking",
      "liquidity-pools",
      "impermanent-loss",
      "market-integrity",
      "staking-tradeoffs",
    ],
    publishedDate: "2026-02-05",
    lastUpdated: "2026-02-05",
    author: "Jeremy R DeYoung",
  },
  {
    route: ROUTES.BLOG_BUYBACK_BURN_VS_ALLOCATE,
    title: "Buyback-and-Burn vs Buyback-and-Allocate: Two Paths, One Governance Standard",
    description:
      "How buyback-and-burn and buyback-and-allocate are two legitimate paths for routing platform-generated value into token economics—both requiring the same governance standard: bounded policy, transparent rationale, and verifiable disclosure.",
    category: BlogCategory.Tokenomics,
    tags: [
      "buyback",
      "buyback-and-burn",
      "buyback-and-allocate",
      "token-buybacks",
      "supply-discipline",
      "value-accrual",
      "token-economics",
      "governance",
      "DPCF",
      "distributable-platform-cash-flow",
    ],
    publishedDate: "2026-02-03",
    lastUpdated: "2026-02-03",
    author: "Jeremy R DeYoung",
  },
  {
    route: ROUTES.BLOG_VALUE_ACCRUAL_ENGINE,
    title: "The Value-Accrual Engine: How DPCF Becomes Token Economics",
    description:
      "How Distributable Platform Cash Flow (DPCF) becomes token economics through disciplined allocation—routing value into supply discipline, ecosystem programs, and treasury stability through bounded policy, predictable cadence, and verifiable outcomes.",
    category: BlogCategory.Tokenomics,
    tags: [
      "value-accrual",
      "DPCF",
      "distributable-platform-cash-flow",
      "token-economics",
      "value-accrual-engine",
      "token-buybacks",
      "supply-discipline",
      "ecosystem-programs",
      "treasury-stability",
      "token-allocation",
    ],
    publishedDate: "2026-02-02",
    lastUpdated: "2026-02-02",
    author: "Jeremy R DeYoung",
  },
  {
    route: ROUTES.BLOG_READINESS_GATES_DISCLOSURES,
    title: 'Readiness Gates and Disclosures: What "Institution-Ready" Means in Practice',
    description:
      'How readiness gates and disclosures define what "institution-ready" actually means: operational readiness, disclosure readiness, supply transparency, emissions schedules, liquidity posture, staking transparency, and governance accountability—turning credibility into a feature rather than a claim.',
    category: BlogCategory.Tokenomics,
    tags: [
      "institution-ready",
      "readiness-gates",
      "readiness-disclosures",
      "institutional-investors",
      "supply-disclosure",
      "emissions-disclosure",
      "liquidity-disclosure",
      "staking-transparency",
      "governance-accountability",
      "token-economics",
    ],
    publishedDate: "2026-01-30",
    lastUpdated: "2026-01-30",
    author: "Jeremy R DeYoung",
  },
  {
    route: ROUTES.BLOG_GOVERNANCE_APPROVED_PARAMETER_CHANGES,
    title:
      "Governance-Approved Parameter Changes: How Staking Evolves Without Breaking Trust",
    description:
      "How governance-approved parameter changes enable staking programs to evolve without breaking trust—creating accountable evolution through proposals, review periods, recorded decisions, and clear implementation timelines.",
    category: BlogCategory.Tokenomics,
    tags: [
      "governance-approved-changes",
      "staking-governance",
      "parameter-changes",
      "staking-evolution",
      "staking-parameters",
      "governance-process",
      "rule-changes",
      "staking-caps",
      "lock-duration",
      "staking-multipliers",
    ],
    publishedDate: "2026-01-29",
    lastUpdated: "2026-01-29",
    author: "Jeremy R DeYoung",
  },
  {
    route: ROUTES.BLOG_TRANSPARENCY_STACK,
    title:
      "The Transparency Stack: Dashboards, Reporting Cadence, and What Stakeholders Can Verify",
    description:
      "How a transparency stack creates structured, repeatable transparency through dashboards, reporting cadence, and verification—turning transparency from a personality trait into infrastructure that reduces information asymmetry and builds credibility.",
    category: BlogCategory.Tokenomics,
    tags: [
      "transparency-stack",
      "token-transparency",
      "transparency-dashboards",
      "reporting-cadence",
      "supply-transparency",
      "emissions-transparency",
      "liquidity-transparency",
      "staking-transparency",
      "governance-transparency",
      "utility-transparency",
    ],
    publishedDate: "2026-01-28",
    lastUpdated: "2026-01-28",
    author: "Jeremy R DeYoung",
  },
  {
    route: ROUTES.BLOG_THIRTY_MONTH_EMISSIONS_PLAN,
    title: "The 30-Month Emissions Plan: Reading the Schedule Like a Risk Manager",
    description:
      "How to read a 30-month emissions plan like a risk manager: identifying stress windows, interpreting phases, connecting schedule mechanics to market structure, and understanding how emissions interact with liquidity and price discovery.",
    category: BlogCategory.Tokenomics,
    tags: [
      "emissions-plan",
      "token-emissions",
      "emissions-schedule",
      "supply-emissions",
      "emissions-risk",
      "token-economics",
      "emissions-phases",
      "liquidity-risk",
      "market-structure",
      "supply-schedule",
    ],
    publishedDate: "2026-01-27",
    lastUpdated: "2026-01-27",
    author: "Jeremy R DeYoung",
  },
  {
    route: ROUTES.BLOG_UNLOCKS_CLIFFS_LINEAR_VESTING,
    title:
      "Unlocks, Cliffs, and Linear Vesting: How Release Schedules Protect Market Integrity",
    description:
      "How unlocks, cliffs, and linear vesting create disciplined release schedules that protect market integrity by reducing uncertainty, aligning time horizons, and preventing supply shocks that destabilize price discovery.",
    category: BlogCategory.Tokenomics,
    tags: [
      "token-vesting",
      "unlock-schedules",
      "vesting-cliffs",
      "linear-vesting",
      "token-unlocks",
      "supply-release",
      "market-integrity",
      "vesting-schedules",
      "token-distribution",
      "supply-discipline",
    ],
    publishedDate: "2026-01-26",
    lastUpdated: "2026-01-26",
    author: "Jeremy R DeYoung",
  },
  {
    route: ROUTES.BLOG_TRANSPARENT_ORDER_BOOKS,
    title:
      "Transparent Order Books and Credible Price Discovery: What to Watch Post-Launch",
    description:
      "What transparent order books signal about market integrity, how to evaluate healthy price discovery post-launch, and what participants should watch to assess credibility rather than hype—spreads, depth, venue divergence, and market structure.",
    category: BlogCategory.Tokenomics,
    tags: [
      "transparent-order-books",
      "price-discovery",
      "market-integrity",
      "order-book-depth",
      "market-spreads",
      "venue-divergence",
      "market-manipulation",
      "trading-transparency",
      "post-launch-evaluation",
      "market-structure",
    ],
    publishedDate: "2026-01-23",
    lastUpdated: "2026-01-23",
    author: "Jeremy R DeYoung",
  },
  {
    route: ROUTES.BLOG_ALLOCATION_BANDS_EXPLAINED,
    title:
      "Allocation Bands Explained: Burn, Programs, Treasury—and the 'Why' Behind the Ranges",
    description:
      "How allocation bands create bounded flexibility in token economics by defining ranges for burn, programs, and treasury allocations—turning value routing into a disciplined operating standard with governance oversight.",
    category: BlogCategory.Tokenomics,
    tags: [
      "allocation-bands",
      "token-allocation",
      "burn-programs",
      "treasury-allocation",
      "token-economics",
      "value-routing",
      "governance-oversight",
      "token-distribution",
      "supply-discipline",
      "program-funding",
    ],
    publishedDate: "2026-01-22",
    lastUpdated: "2026-01-22",
    author: "Jeremy R DeYoung",
  },
  {
    route: ROUTES.BLOG_LIQUIDITY_CONTROLS_101,
    title: "Liquidity Controls 101: Why 'More Listings' Isn't a Strategy",
    description:
      "Why liquidity controls matter for token launches: staged access, bounded inventory, monitoring expectations, and clear disclosure to create usable markets instead of chaotic price discovery.",
    category: BlogCategory.Tokenomics,
    tags: [
      "liquidity-controls",
      "token-liquidity",
      "liquidity-strategy",
      "market-makers",
      "staged-access",
      "inventory-discipline",
      "market-integrity",
      "price-discovery",
      "token-listings",
      "liquidity-governance",
    ],
    publishedDate: "2026-01-21",
    lastUpdated: "2026-01-21",
    author: "Jeremy R DeYoung",
  },
  {
    route: ROUTES.BLOG_MEASURING_UTILITY,
    title:
      "Measuring Utility: The On-Chain and Platform Metrics That Prove Tokenomics Works",
    description:
      "How to measure token utility through on-chain metrics, platform analytics, and behavioral indicators that prove tokenomics is working—turning utility claims into verifiable evidence instead of vanity metrics.",
    category: BlogCategory.Tokenomics,
    tags: [
      "token-utility-metrics",
      "on-chain-metrics",
      "platform-analytics",
      "ubt-metrics",
      "staking-metrics",
      "tokenomics-measurement",
      "utility-measurement",
      "token-economics",
      "utility-proof",
      "behavioral-metrics",
    ],
    publishedDate: "2026-01-20",
    lastUpdated: "2026-01-20",
    author: "Jeremy R DeYoung",
  },
  {
    route: ROUTES.BLOG_STAKING_ANALYTICS_DASHBOARD,
    title: "Staking Analytics Dashboard: What We Track and Why It Matters",
    description:
      "How staking analytics dashboards provide observability into commitment, time horizon, and unlock risk—turning staking from a black box into a credibility mechanism that reinforces token economics through transparency.",
    category: BlogCategory.Tokenomics,
    tags: [
      "staking-analytics",
      "staking-dashboard",
      "staking-metrics",
      "lock-duration",
      "unlock-schedule",
      "staking-transparency",
      "token-staking",
      "staking-governance",
      "staking-observability",
      "commitment-metrics",
    ],
    publishedDate: "2026-01-19",
    lastUpdated: "2026-01-19",
    author: "Jeremy R DeYoung",
  },
  {
    route: ROUTES.BLOG_DISCLOSURE_DISCIPLINE,
    title:
      "Disclosure Discipline: What We Publish About Supply, Emissions, and Liquidity",
    description:
      "How disclosure discipline turns tokenomics into an operating standard by making supply, emissions, and liquidity transparent, verifiable, and predictable—reducing information asymmetry and building credible market structure.",
    category: BlogCategory.Tokenomics,
    tags: [
      "disclosure-discipline",
      "supply-transparency",
      "emissions-disclosure",
      "liquidity-disclosure",
      "tokenomics-transparency",
      "market-integrity",
      "information-asymmetry",
      "token-economics",
      "verifiable-disclosures",
    ],
    publishedDate: "2026-01-15",
    lastUpdated: "2026-01-15",
    author: "Jeremy R DeYoung",
  },
  {
    route: ROUTES.BLOG_UTILITY_BOUND_TOKENS_101,
    title: "Utility-Bound Tokens (UBTs) 101: The Non-Transferable Reward Primitive",
    description:
      "How Utility-Bound Tokens (UBTs) keep incentives inside the ecosystem by rewarding contribution in non-transferable units that unlock platform utility, creating aligned participation instead of sell pressure.",
    category: BlogCategory.Tokenomics,
    tags: [
      "utility-bound-tokens",
      "ubts",
      "non-transferable-tokens",
      "reward-primitive",
      "token-incentives",
      "staking-rewards",
      "ecosystem-alignment",
      "token-economics",
      "participation-rewards",
      "utility-tokens",
    ],
    publishedDate: "2026-01-14",
    lastUpdated: "2026-01-14",
    author: "Jeremy R DeYoung",
  },
  {
    route: ROUTES.BLOG_DEX_LISTING_READINESS,
    title: "DEX Listing Readiness: Liquidity Commitment, Lock-Ins, and Launch Discipline",
    description:
      "How DEX listing readiness requires liquidity commitment, lock-ins, and launch discipline to create orderly price discovery and credible market structure from day one.",
    category: BlogCategory.Tokenomics,
    tags: [
      "dex-listing",
      "liquidity-commitment",
      "lock-ins",
      "launch-discipline",
      "price-discovery",
      "market-structure",
      "liquidity-planning",
      "token-launch",
      "dex-readiness",
      "market-integrity",
    ],
    publishedDate: "2026-01-13",
    lastUpdated: "2026-01-13",
    author: "Jeremy R DeYoung",
  },
  {
    route: ROUTES.BLOG_MARKET_MAKER_INVENTORY_UNLOCKS,
    title: "Market Maker Inventory Unlocks: The 50/25/25 Milestone Model Explained",
    description:
      "How Becoming Alpha's 50/25/25 market maker inventory unlock model ties liquidity releases to market-structure milestones, ensuring governed, accountable, and transparent token economics.",
    category: BlogCategory.Tokenomics,
    tags: [
      "market-maker-inventory",
      "50-25-25-model",
      "liquidity-unlocks",
      "dex-listing",
      "cex-listing",
      "token-economics",
      "market-structure",
      "liquidity-planning",
      "tokenomics",
      "milestone-based-unlocks",
    ],
    publishedDate: "2026-01-12",
    lastUpdated: "2026-01-12",
    author: "Jeremy R DeYoung",
  },
  {
    route: ROUTES.BLOG_LIQUIDITY_PLANNING_TOOLS,
    title:
      "Liquidity Planning Tools: DEX/CEX Allocation, Market Maker Mandates, and Lock Management",
    description:
      "How liquidity planning tools help teams allocate liquidity across DEXs and CEXs, manage market maker relationships through clear mandates, and enforce lock management so the supply story stays truthful and market structure remains credible.",
    category: BlogCategory.Tokenomics,
    tags: [
      "liquidity-planning",
      "dex-allocation",
      "cex-allocation",
      "market-maker-mandates",
      "lock-management",
      "token-liquidity",
      "market-structure",
      "price-discovery",
      "supply-management",
      "launch-infrastructure",
    ],
    publishedDate: "2026-01-09",
    lastUpdated: "2026-01-09",
    author: "Jeremy R DeYoung",
  },
  {
    route: ROUTES.BLOG_TALENT_MARKETPLACE_ESCROW,
    title:
      "Talent Marketplace Escrow: Secure Service Delivery With Milestone-Linked Payments",
    description:
      "How talent marketplace escrow with milestone-linked payments turns service delivery into a trusted process, protecting founders, professionals, and investors by reducing fraud, disputes, and operational chaos through structured execution contracts.",
    category: BlogCategory.Governance,
    tags: [
      "talent-marketplace",
      "escrow",
      "milestone-linked-payments",
      "service-delivery",
      "secure-payments",
      "contractor-protection",
      "dispute-resolution",
      "operational-execution",
      "web3-marketplace",
      "delivery-infrastructure",
    ],
    publishedDate: "2026-01-07",
    lastUpdated: "2026-01-07",
    author: "Jeremy R DeYoung",
  },
  {
    route: ROUTES.BLOG_PROOF_OF_EXECUTION,
    title:
      "Proof of Execution: Building Verifiable Reputation Through Logged Actions and Milestones",
    description:
      "How Proof of Execution creates a verifiable reputation layer where logged actions and milestone completion become the foundation of credibility, replacing fragile reputation signals with evidence-based trust that compounds over time.",
    category: BlogCategory.Governance,
    tags: [
      "proof-of-execution",
      "verifiable-reputation",
      "logged-actions",
      "milestone-completion",
      "execution-reputation",
      "accountability",
      "reputation-building",
      "investor-trust",
      "credibility",
      "web3-reputation",
      "evidence-based-trust",
    ],
    publishedDate: "2026-01-05",
    lastUpdated: "2026-01-05",
    author: "Jeremy R DeYoung",
  },
  {
    route: ROUTES.BLOG_ALPHA_SHORTLISTS,
    title:
      "Alpha Shortlists: Collaborative Curation for Evaluation, Organization, and Promotion",
    description:
      "How Alpha Shortlists turn social discovery into structured decision-making, creating collaborative curation surfaces that help investors evaluate, organize, and promote opportunities with evidence-first workflows and milestone-driven rigor.",
    category: BlogCategory.Governance,
    tags: [
      "alpha-shortlists",
      "collaborative-curation",
      "investor-curation",
      "due-diligence",
      "evaluation-workflow",
      "pipeline-discipline",
      "investor-organization",
      "promotion-accountability",
      "milestone-driven-curation",
      "web3-curation",
    ],
    publishedDate: "2026-01-03",
    lastUpdated: "2026-01-03",
    author: "Jeremy R DeYoung",
  },
  {
    route: ROUTES.BLOG_ALPHA_SHOWCASES,
    title:
      "Alpha Showcases: Milestone-Linked Presentation Pages Replacing Informal Outreach",
    description:
      "How Alpha Showcases replace informal outreach with structured, verifiable, investor-grade presentation pages that anchor narratives to milestones, attach claims to artifacts, and preserve change history so trust compounds instead of resetting.",
    category: BlogCategory.Governance,
    tags: [
      "alpha-showcases",
      "milestone-linked-presentation",
      "investor-grade-narratives",
      "proof-pages",
      "due-diligence",
      "transparency",
      "accountability",
      "structured-presentation",
      "verifiable-progress",
      "web3-outreach",
      "investor-communication",
    ],
    publishedDate: "2026-01-01",
    lastUpdated: "2026-01-01",
    author: "Jeremy R DeYoung",
  },
  {
    route: ROUTES.BLOG_ELEVATE_TO_ALPHA,
    title:
      "Elevate to Alpha: The Community Incentives Framework Connecting Execution to Rewards",
    description:
      "How the Elevate to Alpha framework ties rewards to milestone-driven execution rather than hype-driven activity, creating compounding trust and investor-grade community incentives that are verifiable, hard to game, and aligned with long-term value creation.",
    category: BlogCategory.Governance,
    tags: [
      "elevate-to-alpha",
      "community-incentives",
      "milestone-driven-rewards",
      "execution-based-incentives",
      "investor-protection",
      "verifiable-execution",
      "anti-gaming",
      "web3-incentives",
      "token-rewards",
      "governance-alignment",
      "truth-engine",
    ],
    publishedDate: "2025-12-30",
    lastUpdated: "2025-12-30",
    author: "Jeremy R DeYoung",
  },
  {
    route: ROUTES.BLOG_ALPHA_AI_ENGINE,
    title: "Alpha AI Engine: Intelligent Matching in a Milestone-Driven Ecosystem",
    description:
      "How the Alpha AI Engine powers intelligent matching and profiling in an investor-first ecosystem, turning milestone-driven execution into relevance signals that reduce noise and improve capital allocation efficiency.",
    category: BlogCategory.Governance,
    tags: [
      "alpha-ai-engine",
      "ai-matching",
      "intelligent-matching",
      "milestone-driven",
      "dynamic-profiling",
      "signal-intelligence",
      "investor-preferences",
      "relevance-engineering",
      "governance",
      "investor-protection",
      "web3-matching",
    ],
    publishedDate: "2025-12-28",
    lastUpdated: "2025-12-28",
    author: "Jeremy R DeYoung",
  },
  {
    route: ROUTES.BLOG_TRUST_STACK,
    title:
      "The Trust Stack: Combining Code, Compliance, and Community to Secure Web3 Investments",
    description:
      "Security in Web3 is often treated like a single lever. Learn why investor-grade platforms need a trust stack—multiple layers that reinforce each other so a single failure doesn't become catastrophic.",
    category: BlogCategory.Security,
    tags: [
      "trust-stack",
      "code-trust",
      "compliance-trust",
      "community-trust",
      "security",
      "compliance",
      "governance",
      "investor-protection",
      "web3-security",
      "security-architecture",
      "layered-security",
      "web3-investments",
    ],
    publishedDate: "2025-12-26",
    lastUpdated: "2025-12-26",
    author: "Jeremy R DeYoung",
  },
  {
    route: ROUTES.BLOG_DYNAMIC_COMPLIANCE,
    title: "Dynamic Compliance: Adapting to Regulatory Change in a Cross-Chain Platform",
    description:
      "Why static compliance fails in Web3. Learn how dynamic compliance as a control plane adapts to regulatory change while preserving user experience, privacy, and security—creating investor-grade resilience.",
    category: BlogCategory.Compliance,
    tags: [
      "dynamic-compliance",
      "regulatory-change",
      "cross-chain",
      "compliance-architecture",
      "policy-versioning",
      "risk-based-controls",
      "investor-confidence",
      "regulatory-resilience",
      "control-plane",
    ],
    publishedDate: "2025-12-24",
    lastUpdated: "2025-12-24",
    author: "Jeremy R DeYoung",
  },
  {
    route: ROUTES.BLOG_SIMULATING_THE_LAUNCH,
    title: "Simulating the Launch: How We Test and Tune Operations Before Go-Live",
    description:
      "Why launch simulations are the missing institutional layer in Web3 ecosystems. Learn how stress testing, adversarial simulation, and incident response drills translate into investor confidence and operational maturity.",
    category: BlogCategory.Operations,
    tags: [
      "launch-simulation",
      "stress-testing",
      "operational-readiness",
      "go-live-testing",
      "load-testing",
      "chain-stress",
      "adversarial-simulation",
      "incident-response",
      "investor-confidence",
      "operational-maturity",
    ],
    publishedDate: "2025-12-23",
    lastUpdated: "2025-12-23",
    author: "Jeremy R DeYoung",
  },
  {
    route: ROUTES.BLOG_FROM_WALL_STREET_TO_WEB3,
    title:
      "From Wall Street to Web3: Adapting Traditional Risk Controls for Crypto Launches",
    description:
      "How institutional risk controls translate to Web3 launch infrastructure. Learn why separation of duties, independent verification, auditability, and recovery planning matter for investor-grade crypto platforms.",
    category: BlogCategory.Security,
    tags: [
      "risk-controls",
      "traditional-finance",
      "web3",
      "security",
      "governance",
      "compliance",
      "investor-protection",
      "institutional-grade",
      "security-by-design",
      "audit-trails",
    ],
    publishedDate: "2025-12-22",
    lastUpdated: "2025-12-22",
    author: "Jeremy R DeYoung",
  },
  {
    route: ROUTES.BLOG_ENFORCING_ACCOUNTABILITY_POST_LAUNCH_GOVERNANCE_OVERSIGHT,
    title:
      "Enforcing Accountability: Post-Launch Governance Oversight as Investor Protection",
    description:
      "Why post-launch accountability is the missing layer in Web3 ecosystems. Learn how governance oversight protects investors after launch when incentives collide, markets move, and governance is tested.",
    category: BlogCategory.Governance,
    tags: [
      "governance",
      "post-launch",
      "accountability",
      "investor-protection",
      "oversight",
      "transparency",
      "treasury",
      "governance-capture",
      "investor-confidence",
    ],
    publishedDate: "2025-12-20",
    lastUpdated: "2025-12-20",
    author: "Jeremy R DeYoung",
  },
  {
    route: ROUTES.BLOG_RESILIENCE_AS_A_SERVICE,
    title: "Resilience as a Service: Designing Systems to Survive Worst-Case Scenarios",
    description:
      "Understanding resilience as a service in Web3 launchpad ecosystems. Learn how to design systems that fail gracefully, reduce blast radius, and preserve trust when worst-case scenarios occur.",
    category: BlogCategory.Security,
    tags: [
      "resilience",
      "system-design",
      "worst-case-scenarios",
      "incident-response",
      "architecture",
      "blast-radius",
      "circuit-breakers",
      "investor-protection",
      "trust",
      "platform-reliability",
    ],
    publishedDate: "2025-12-19",
    lastUpdated: "2025-12-19",
    author: "Jeremy R DeYoung",
  },
  {
    route: ROUTES.BLOG_BEYOND_THE_AUDIT_CONTINUOUS_SECURITY_VALIDATION,
    title: "Beyond the Audit: Continuous Security Validation for Investor Confidence",
    description:
      "Understanding why security audits are necessary but not sufficient. Learn how continuous security validation maintains trust through upgrades, integrations, and stress events for investor-grade platforms.",
    category: BlogCategory.Security,
    tags: [
      "security",
      "audits",
      "continuous-validation",
      "investor-confidence",
      "security-by-design",
      "monitoring",
      "incident-response",
      "risk-management",
      "post-audit-verification",
      "security-lifecycle",
    ],
    publishedDate: "2025-12-18",
    lastUpdated: "2025-12-18",
    author: "Jeremy R DeYoung",
  },
  {
    route: ROUTES.BLOG_GUARDIANS_OF_CROSS_CHAIN_INTEGRITY,
    title:
      "Guardians of Cross-Chain Integrity: How We Mitigate Third-Party Risks in an Omnichain Ecosystem",
    description:
      "Understanding third-party risk in omnichain systems. Learn how to manage dependencies, reduce blast radius, and maintain cross-chain integrity when messaging providers, RPC endpoints, and compliance vendors fail.",
    category: BlogCategory.Security,
    tags: [
      "cross-chain",
      "omnichain",
      "third-party-risk",
      "dependency-management",
      "messaging-layers",
      "rpc-providers",
      "compliance-vendors",
      "supply-integrity",
      "blast-radius",
      "risk-mitigation",
    ],
    publishedDate: "2025-12-17",
    lastUpdated: "2025-12-17",
    author: "Jeremy R DeYoung",
  },
  {
    route: ROUTES.BLOG_LAYERZERO_OFT,
    title: "LayerZero OFT Explained: How ALPHA Moves Natively Across Chains",
    description:
      "Learn how ALPHA token uses LayerZero V2 OApp for native cross-chain transfers. Understanding OFT primitives, supply integrity, and operational monitoring for omnichain tokens.",
    category: BlogCategory.MultichainOFT,
    tags: [
      "oft",
      "layerzero",
      "oapp",
      "cross-chain",
      "token-architecture",
      "supply-integrity",
    ],
    publishedDate: "2025-12-16",
    lastUpdated: "2025-12-16",
    author: "Jeremy R DeYoung",
  },
  {
    route: ROUTES.BLOG_BRIDGES_ATTACK_SURFACE,
    title:
      "Why Bridges Are the Biggest Attack Surface in Web3 (And How We Reduce the Blast Radius)",
    description:
      "Understanding why bridges are the biggest attack surface in Web3 and how blast radius reduction limits damage. Learn about risk containment, operational controls, and transparency in cross-chain infrastructure.",
    category: BlogCategory.Security,
    tags: [
      "bridges",
      "attack-surface",
      "threat-model",
      "risk-containment",
      "cross-chain",
    ],
    publishedDate: "2025-12-14",
    lastUpdated: "2025-12-14",
    author: "Jeremy R DeYoung",
  },
  {
    route: ROUTES.BLOG_COMPLIANCE_FIRST,
    title:
      "Compliance-First Launch Architecture: KYC/AML, Sanctions, Geo Controls, and Audit Trails",
    description:
      "How compliance-first architecture enhances platform safety. Understanding KYC/AML flows, sanctions screening, geo restrictions, and audit logging in blockchain launchpads.",
    category: BlogCategory.Compliance,
    tags: ["compliance", "kyc", "aml", "sanctions", "geofencing", "audit-trails"],
    publishedDate: "2025-12-12",
    lastUpdated: "2025-12-12",
    author: "Jeremy R DeYoung",
  },
  {
    route: ROUTES.BLOG_SECURITY_BY_DESIGN,
    title:
      "Security-By-Design Isn't a Slogan: Defense-in-Depth + Zero Trust for a Launchpad Ecosystem",
    description:
      "How security-by-design becomes real in practice. Understanding defense-in-depth, zero trust architecture, observability, and incident response for launchpad ecosystems.",
    category: BlogCategory.Security,
    tags: [
      "security-by-design",
      "defense-in-depth",
      "zero-trust",
      "observability",
      "operations",
    ],
    publishedDate: "2025-12-10",
    lastUpdated: "2025-12-10",
    author: "Jeremy R DeYoung",
  },
  {
    route: ROUTES.BLOG_OFT_VS_WRAPPED,
    title: "OFT vs Wrapped Tokens: Security Trade-Offs and Liquidity Fragmentation",
    description:
      "Comparing native omnichain tokens (OFT) with wrapped tokens. Understanding security trade-offs, trust assumptions, custody models, and liquidity fragmentation implications.",
    category: BlogCategory.MultichainOFT,
    tags: ["oft", "wrapped-tokens", "bridges", "custody", "liquidity", "risk-model"],
    publishedDate: "2025-12-08",
    lastUpdated: "2025-12-08",
    author: "Jeremy R DeYoung",
  },
  {
    route: ROUTES.BLOG_SUPPLY_INTEGRITY,
    title:
      "Supply Integrity Across Chains: Mint/Burn Accounting, Inflight Tracking, and Failure Recovery",
    description:
      "Understanding supply integrity in omnichain systems. Learn how mint/burn accounting, inflight tracking, and failure recovery preserve trust across chains even when things go wrong.",
    category: BlogCategory.MultichainOFT,
    tags: [
      "supply-integrity",
      "mint-burn",
      "inflight",
      "recovery",
      "cross-chain",
      "observability",
    ],
    publishedDate: "2025-12-06",
    lastUpdated: "2025-12-06",
    author: "Jeremy R DeYoung",
  },
  {
    route: ROUTES.BLOG_VENTURE_ONBOARDING_READINESS_VERIFICATION,
    title:
      "Venture Onboarding and Readiness Verification: Proving Credibility Before Launch",
    description:
      "Understanding how launchpad venture onboarding wizard ensures founders prove credible foundations before launching, reducing investor risk. Learn about step-by-step readiness verification and interactive checklist tracking.",
    category: BlogCategory.Launchpad,
    tags: [
      "launchpad",
      "venture-onboarding",
      "readiness-verification",
      "investor-protection",
      "credibility",
    ],
    publishedDate: "2025-12-04",
    lastUpdated: "2025-12-04",
    author: "Jeremy R DeYoung",
  },
  {
    route: ROUTES.BLOG_TOKENOMICS_VERIFY,
    title:
      "Tokenomics You Can Verify: Structured Vesting, Predictable Emissions, and Transparent Timelines",
    description:
      "Understanding verifiable tokenomics. Learn how structured vesting, predictable emissions, and transparent timelines turn tokenomics into infrastructure rather than narrative.",
    category: BlogCategory.Tokenomics,
    tags: ["tokenomics", "vesting", "emissions", "transparency", "governance", "trust"],
    publishedDate: "2025-12-02",
    lastUpdated: "2025-12-02",
    author: "Jeremy R DeYoung",
  },
  {
    route: ROUTES.BLOG_TOKEN_DESIGN_TOOLS_VESTING_SCHEDULERS,
    title:
      "Token Design Tools and Vesting Schedulers: Building Institutional-Grade Tokenomics",
    description:
      "Understanding how launchpad token design tools enable founders to create sophisticated vesting schedules and simulate tokenomics before deployment. Learn about visual vesting designer and tokenomics simulator.",
    category: BlogCategory.Launchpad,
    tags: ["launchpad", "token-design", "vesting", "tokenomics", "simulator", "fdv"],
    publishedDate: "2025-11-30",
    lastUpdated: "2025-11-30",
    author: "Jeremy R DeYoung",
  },
  {
    route: ROUTES.BLOG_POST_LAUNCH_OPERATING_CONSOLE,
    title:
      "Post-Launch Operating Console: Monitoring Treasury, Metrics, and Governance Across Chains",
    description:
      "Understanding how post-launch operating console provides founders with comprehensive monitoring: multi-chain treasury, token metrics, adoption KPIs, and governance activity. Learn how the console enables ongoing venture management.",
    category: BlogCategory.Launchpad,
    tags: [
      "launchpad",
      "post-launch",
      "monitoring",
      "treasury",
      "metrics",
      "governance",
      "cross-chain",
    ],
    publishedDate: "2025-11-28",
    lastUpdated: "2025-11-28",
    author: "Jeremy R DeYoung",
  },
  {
    route: ROUTES.BLOG_CROSS_CHAIN_VESTING,
    title:
      "Cross-Chain Vesting: Managing Token Distribution and Unlocks Across Multiple Networks",
    description:
      "Understanding how cross-chain vesting enables token distribution schedules that work seamlessly across multiple blockchains, maintaining supply integrity and enabling flexible unlock strategies.",
    category: BlogCategory.Tokenomics,
    tags: [
      "vesting",
      "cross-chain",
      "token-distribution",
      "supply-integrity",
      "layerzero",
      "treasury",
    ],
    publishedDate: "2025-11-26",
    lastUpdated: "2025-11-26",
    author: "Jeremy R DeYoung",
  },
  {
    route: ROUTES.BLOG_CROSS_CHAIN_IDENTITY,
    title: "Cross-Chain Identity and Reputation: Making Trust Portable Across Networks",
    description:
      "Understanding cross-chain identity and reputation. Learn how portable trust, verifiable credentials, and privacy-aware design make trust move at the same speed as value across Web3 networks.",
    category: BlogCategory.Identity,
    tags: [
      "identity",
      "reputation",
      "credentials",
      "privacy",
      "sybil-resistance",
      "cross-chain",
    ],
    publishedDate: "2025-11-24",
    lastUpdated: "2025-11-24",
    author: "Jeremy R DeYoung",
  },
  {
    route: ROUTES.BLOG_CROSS_CHAIN_BRIDGES_OFT,
    title:
      "How Cross-Chain Bridges Actually Work Under the Hood: An OFT Messaging Edition",
    description:
      "Understanding how cross-chain bridges work under the hood. Learn about OFT messaging architecture, burn-and-mint mechanics, message validation, and how Security-By-Design principles make bridges secure.",
    category: BlogCategory.MultichainOFT,
    tags: [
      "bridges",
      "cross-chain",
      "oft",
      "messaging",
      "layerzero",
      "validation",
      "failure-modes",
    ],
    publishedDate: "2025-11-22",
    lastUpdated: "2025-11-22",
    author: "Jeremy R DeYoung",
  },
  {
    route: ROUTES.BLOG_ZERO_KNOWLEDGE_KYC,
    title: "Zero-Knowledge KYC: Verifying Identity Without Revealing PII",
    description:
      "Understanding how zero-knowledge proofs enable platforms to verify KYC/AML compliance without storing or exposing personal information. Learn how ZK proofs can prove eligibility without revealing underlying data.",
    category: BlogCategory.Privacy,
    tags: [
      "zero-knowledge",
      "kyc",
      "privacy",
      "compliance",
      "pii",
      "zk-proofs",
      "identity-verification",
    ],
    publishedDate: "2025-11-20",
    lastUpdated: "2025-11-20",
    author: "Jeremy R DeYoung",
  },
  {
    route: ROUTES.BLOG_WEB3_WALLET_AUTHENTICATION,
    title:
      "Web3 Wallet Authentication: Connecting EVM and Solana Wallets With CAIP Standards",
    description:
      "Understanding how Alpha supports wallet authentication for both EVM and Solana ecosystems using CAIP-2 and CAIP-10 standards. Learn how WalletConnect and challenge-response authentication enable secure, chain-agnostic wallet login.",
    category: BlogCategory.Identity,
    tags: [
      "web3",
      "wallet-authentication",
      "caip",
      "evm",
      "solana",
      "walletconnect",
      "cross-chain",
    ],
    publishedDate: "2025-11-18",
    lastUpdated: "2025-11-18",
    author: "Jeremy R DeYoung",
  },
  {
    route: ROUTES.BLOG_COMPLIANCE_AWARE_LAUNCH_WORKFLOWS,
    title:
      "Compliance-Aware Launch Workflows: Jurisdiction-Aware Risk Assessment and Legal Readiness",
    description:
      "Understanding how compliance-aware launch flow ensures ventures meet regulatory requirements before launch through jurisdiction-aware risk assessment, dynamic questionnaires, and legal document management.",
    category: BlogCategory.Launchpad,
    tags: [
      "launchpad",
      "compliance",
      "risk-assessment",
      "jurisdiction",
      "legal-readiness",
      "questionnaire",
    ],
    publishedDate: "2025-11-16",
    lastUpdated: "2025-11-16",
    author: "Jeremy R DeYoung",
  },
  {
    route: ROUTES.BLOG_INCIDENT_RESPONSE_OMNICHAIN,
    title:
      "Incident Response for Omnichain Systems: How to Pause Safely and Recover User Funds",
    description:
      "Understanding incident response in omnichain systems. Learn how to pause safely, recover user funds, maintain supply integrity, and coordinate across chains during incidents.",
    category: BlogCategory.Operations,
    tags: ["incident-response", "pausing", "recovery", "cross-chain", "supply-integrity"],
    publishedDate: "2025-11-14",
    lastUpdated: "2025-11-14",
    author: "Jeremy R DeYoung",
  },
  {
    route: ROUTES.BLOG_ANONYMOUS_CROSS_CHAIN_REPUTATION,
    title:
      "Anonymous Cross-Chain Reputation: Building Trust Signals Without Identity Disclosure",
    description:
      "Understanding how reputation can be built and verified across chains using pseudonymous identifiers without linking to real-world identity. Learn how behavioral signals create reputation without PII.",
    category: BlogCategory.Identity,
    tags: [
      "reputation",
      "cross-chain",
      "privacy",
      "identity",
      "pseudonymous",
      "trust-signals",
    ],
    publishedDate: "2025-11-12",
    lastUpdated: "2025-11-12",
    author: "Jeremy R DeYoung",
  },
  {
    route: ROUTES.BLOG_MULTI_SITE_ECOSYSTEM_ARCHITECTURE,
    title:
      "Multi-Site Ecosystem Architecture: Building a Unified Platform Across 9 Specialized Sites",
    description:
      "Understanding how Alpha's 9-site architecture enables specialized user experiences while maintaining unified authentication and seamless navigation. Learn how cross-site SSO works and why this architecture was chosen.",
    category: BlogCategory.Architecture,
    tags: [
      "architecture",
      "multi-site",
      "sso",
      "authentication",
      "ecosystem",
      "navigation",
    ],
    publishedDate: "2025-11-10",
    lastUpdated: "2025-11-10",
    author: "Jeremy R DeYoung",
  },
  {
    route: ROUTES.BLOG_LAUNCHPAD_MILESTONE_MANAGEMENT,
    title:
      "Launchpad Milestone Management: Tracking Progress With Evidence and Independent Review",
    description:
      "Understanding how milestone management system enables founders to track venture progress with objective evidence, independent review requests, and transparent proof tracking. Learn how this creates accountability and investor confidence.",
    category: BlogCategory.Launchpad,
    tags: [
      "launchpad",
      "milestones",
      "progress-tracking",
      "accountability",
      "evidence",
      "investor-confidence",
    ],
    publishedDate: "2025-11-08",
    lastUpdated: "2025-11-08",
    author: "Jeremy R DeYoung",
  },
  {
    route: ROUTES.BLOG_INFLUENCER_CREATOR_PLATFORM,
    title:
      "Influencer and Creator Platform: Building Web3's Visibility Program and Brand Ambassador Network",
    description:
      "Understanding how influencer platform enables creators to discover campaigns, track performance, and earn rewards through the Alpha Visibility Program, creating a sustainable creator economy.",
    category: BlogCategory.Platform,
    tags: [
      "influencer",
      "creator-platform",
      "campaigns",
      "visibility-program",
      "creator-economy",
      "rewards",
    ],
    publishedDate: "2025-11-06",
    lastUpdated: "2025-11-06",
    author: "Jeremy R DeYoung",
  },
  {
    route: ROUTES.BLOG_INSTITUTIONAL_GRADE_SECURITY,
    title: "Designing Cross-Chain Systems for Institutional-Grade Security",
    description:
      "Understanding what institutional-grade security means in cross-chain systems. Learn about explicit threat models, accounting integrity, bounded risk, and how architecture determines whether trust can scale.",
    category: BlogCategory.Security,
    tags: [
      "institutional-security",
      "threat-model",
      "accounting",
      "controls",
      "cross-chain",
    ],
    publishedDate: "2025-11-04",
    lastUpdated: "2025-11-04",
    author: "Jeremy R DeYoung",
  },
  {
    route: ROUTES.BLOG_BRIDGE_UX_SECURITY,
    title:
      "The Bridge UX Security Checklist: Confirmations, Fees, Chain Selection, and Failure States",
    description:
      "Understanding why bridge UX is a security boundary. Learn how to design safe chain selection, clear confirmations, transparent fees, and honest failure states that prevent user loss.",
    category: BlogCategory.UXSecurity,
    tags: ["ux", "bridges", "risk-communication", "fees", "finality", "failure-states"],
    publishedDate: "2025-11-01",
    lastUpdated: "2025-11-01",
    author: "Jeremy R DeYoung",
  },
  {
    route: ROUTES.BLOG_PRIVACY_PRESERVING_COMPLIANCE,
    title:
      "Privacy-Preserving Compliance: Meeting AML/CTF Requirements While Maintaining Anonymity",
    description:
      "Understanding how compliance doesn't require full identity disclosure. Learn how sanctions screening, geo-restrictions, and AML checks can work with pseudonymous or anonymous participants.",
    category: BlogCategory.Compliance,
    tags: [
      "privacy",
      "compliance",
      "aml",
      "ctf",
      "sanctions",
      "geo-restrictions",
      "anonymity",
    ],
    publishedDate: "2025-10-30",
    lastUpdated: "2025-10-30",
    author: "Jeremy R DeYoung",
  },
  {
    route: ROUTES.BLOG_PROFESSIONAL_NETWORKING_WEB3,
    title:
      "Professional Networking in Web3: Verified Identities, Reputation Systems, and Deal Rooms",
    description:
      "Understanding how networking site enables Web3 professionals to connect with verified identities, build reputation, and collaborate in secure deal rooms, creating a trusted professional network.",
    category: BlogCategory.Platform,
    tags: [
      "networking",
      "web3",
      "verified-identities",
      "reputation",
      "deal-rooms",
      "professional-network",
    ],
    publishedDate: "2025-10-28",
    lastUpdated: "2025-10-28",
    author: "Jeremy R DeYoung",
  },
  {
    route: ROUTES.BLOG_SMART_CONTRACT_AUDITS,
    title:
      "Smart Contract Audits Explained: What They Prove, What They Don't, and How to Read Them Without Getting Fooled",
    description:
      "Understanding smart contract audits. Learn what audits prove, what they don't cover, how to read audit reports critically, and how Security-By-Design systems remain secure after audits.",
    category: BlogCategory.Security,
    tags: [
      "audits",
      "smart-contracts",
      "verification",
      "risk-model",
      "security-by-design",
    ],
    publishedDate: "2025-10-26",
    lastUpdated: "2025-10-26",
    author: "Jeremy R DeYoung",
  },
  {
    route: ROUTES.BLOG_MONITORING_THREAT_DETECTION,
    title:
      "Monitoring and Threat Detection: What We Log, What We Alert On, and Why It Matters",
    description:
      "Understanding monitoring and threat detection in omnichain systems. Learn what security-relevant events are logged, which metrics matter, and how disciplined monitoring prevents incidents.",
    category: BlogCategory.Operations,
    tags: [
      "monitoring",
      "threat-detection",
      "observability",
      "alerts",
      "incident-response",
    ],
    publishedDate: "2025-10-24",
    lastUpdated: "2025-10-24",
    author: "Jeremy R DeYoung",
  },
  {
    route: ROUTES.BLOG_SECURE_KEY_MANAGEMENT,
    title:
      "Secure Key Management for Normal Humans: Wallets, Passkeys, and Recovery Without Losing Your Funds—or Your Sanity",
    description:
      "Understanding secure key management for everyday users. Learn about wallets, passkeys, recovery mechanisms, and how Security-By-Design protects users without demanding perfection.",
    category: BlogCategory.UserSecurity,
    tags: [
      "key-management",
      "wallets",
      "passkeys",
      "recovery",
      "phishing",
      "user-safety",
    ],
    publishedDate: "2025-10-22",
    lastUpdated: "2025-10-22",
    author: "Jeremy R DeYoung",
  },
  {
    route: ROUTES.BLOG_GOVERNANCE_PARTICIPATION_WITHOUT_IDENTITY,
    title:
      "Governance Participation Without Identity: Anonymous Voting and Proposal Systems",
    description:
      "Understanding how governance can function effectively with pseudonymous participants using reputation-based voting power. Learn how staking and reputation determine governance influence without identity disclosure.",
    category: BlogCategory.Governance,
    tags: ["governance", "voting", "anonymity", "reputation", "staking", "pseudonymous"],
    publishedDate: "2025-10-20",
    lastUpdated: "2025-10-20",
    author: "Jeremy R DeYoung",
  },
  {
    route: ROUTES.BLOG_COMPREHENSIVE_AUDIT_LOGGING,
    title:
      "Comprehensive Audit Logging: Building Provable Compliance and Security Accountability",
    description:
      "Understanding how comprehensive audit logging creates an immutable record of all platform actions, enabling provable compliance, security incident investigation, and regulatory accountability.",
    category: BlogCategory.Operations,
    tags: [
      "audit-logging",
      "compliance",
      "security",
      "accountability",
      "transparency",
      "opensearch",
    ],
    publishedDate: "2025-10-18",
    lastUpdated: "2025-10-18",
    author: "Jeremy R DeYoung",
  },
  {
    route: ROUTES.BLOG_TWO_FACTOR_AUTHENTICATION,
    title:
      "2FA for Crypto Accounts: TOTP vs Email vs Passkeys (and When to Require Each)",
    description:
      "Understanding two-factor authentication for crypto accounts. Learn about TOTP, email codes, passkeys, when to require each method, and how Security-By-Design layers authentication controls.",
    category: BlogCategory.UserSecurity,
    tags: ["2fa", "authentication", "totp", "passkeys", "account-security", "phishing"],
    publishedDate: "2025-10-16",
    lastUpdated: "2025-10-16",
    author: "Jeremy R DeYoung",
  },
  {
    route: ROUTES.BLOG_END_TO_END_ENCRYPTION,
    title:
      "End-to-End Encrypted Messaging in Fintech Workflows: What E2EE Protects (and What It Doesn't)",
    description:
      "Understanding end-to-end encryption in fintech workflows. Learn what E2EE protects, where its protection ends, and how to design encrypted communication systems that enhance trust.",
    category: BlogCategory.PrivacySecurity,
    tags: ["encryption", "e2ee", "messaging", "privacy", "threat-model", "compliance"],
    publishedDate: "2025-10-14",
    lastUpdated: "2025-10-14",
    author: "Jeremy R DeYoung",
  },
  {
    route: ROUTES.BLOG_CROSS_CHAIN_CREDENTIAL_SYSTEMS,
    title: "Cross-Chain Credential Systems: Portable Trust Without PII",
    description:
      "Understanding how credentials can be portable across chains without exposing personal data. Learn how verifiable credentials and attestations enable trust portability while preserving privacy.",
    category: BlogCategory.Identity,
    tags: [
      "credentials",
      "cross-chain",
      "verifiable-credentials",
      "privacy",
      "trust",
      "layerzero",
    ],
    publishedDate: "2025-10-12",
    lastUpdated: "2025-10-12",
    author: "Jeremy R DeYoung",
  },
  {
    route: ROUTES.BLOG_PROFILE_MANAGEMENT_IDENTITY,
    title:
      "Profile Management and Identity: Unified User Profiles Across a Multi-Site Ecosystem",
    description:
      "Understanding how profile site provides centralized user profile management across Alpha's multi-site ecosystem, enabling consistent identity, security settings, and privacy controls.",
    category: BlogCategory.Identity,
    tags: [
      "profile-management",
      "identity",
      "multi-site",
      "security-settings",
      "privacy-controls",
      "gdpr",
    ],
    publishedDate: "2025-10-10",
    lastUpdated: "2025-10-10",
    author: "Jeremy R DeYoung",
  },
  {
    route: ROUTES.BLOG_RUG_PULLS_LAUNCH_SCAMS,
    title:
      "Rug Pulls and Launch Scams: Red Flags, Due Diligence, and Platform-Level Controls",
    description:
      "Understanding rug pulls and launch scams. Learn about red flags, meaningful due diligence, and how platform-level controls can reduce scam risk and protect users.",
    category: BlogCategory.LaunchIntegrity,
    tags: ["scams", "due-diligence", "launchpads", "risk-controls", "user-protection"],
    publishedDate: "2025-10-08",
    lastUpdated: "2025-10-08",
    author: "Jeremy R DeYoung",
  },
  {
    route: ROUTES.BLOG_AML_CTF_COMPLIANCE,
    title:
      "How AML/CTF Compliance Can Enhance Platform Safety (Without Turning Into Surveillance)",
    description:
      "Understanding how AML/CTF compliance enhances platform safety. Learn about risk-based compliance, data minimization, and how modern platforms meet regulatory obligations without surveillance.",
    category: BlogCategory.Compliance,
    tags: ["aml", "ctf", "privacy", "risk-based", "platform-safety", "compliance"],
    publishedDate: "2025-10-06",
    lastUpdated: "2025-10-06",
    author: "Jeremy R DeYoung",
  },
  {
    route: ROUTES.BLOG_PSEUDONYMOUS_VETTING,
    title:
      "Pseudonymous Vetting: How Authorities Can Verify Legitimacy Without Knowing Who You Are",
    description:
      "Understanding how governance authorities and platforms can vet participants for legitimacy without requiring identity disclosure. Learn how audit logs and compliance checks work with pseudonymous identifiers.",
    category: BlogCategory.Compliance,
    tags: [
      "vetting",
      "pseudonymous",
      "compliance",
      "audit-logs",
      "legitimacy",
      "privacy",
    ],
    publishedDate: "2025-10-04",
    lastUpdated: "2025-10-04",
    author: "Jeremy R DeYoung",
  },
  {
    route: ROUTES.BLOG_ADMIN_DASHBOARD_AND_ANALYTICS,
    title:
      "Admin Dashboard and Analytics: Platform-Wide Monitoring, Compliance Metrics, and User Insights",
    description:
      "Understanding how admin dashboard provides comprehensive platform analytics: real-time statistics, user growth trends, compliance metrics, and geographic distribution for operational decision-making.",
    category: BlogCategory.Operations,
    tags: [
      "admin",
      "analytics",
      "monitoring",
      "compliance-metrics",
      "user-insights",
      "platform-operations",
    ],
    publishedDate: "2025-10-02",
    lastUpdated: "2025-10-02",
    author: "Jeremy R DeYoung",
  },
  {
    route: ROUTES.BLOG_GOVERNANCE_RISKS_OMNICHAIN,
    title:
      "Governance Risks in Omnichain Token Systems (And How to Keep Incentives Aligned)",
    description:
      "Understanding governance risks in omnichain token systems, how supply fragmentation affects governance, and design principles to keep incentives aligned across chains.",
    category: BlogCategory.Governance,
    tags: [
      "governance",
      "omnichain",
      "cross-chain",
      "incentives",
      "manipulation",
      "execution",
    ],
    publishedDate: "2025-09-30",
    lastUpdated: "2025-09-30",
    author: "Jeremy R DeYoung",
  },
  {
    route: ROUTES.BLOG_FAST_BRIDGES_SYSTEMIC_RISK,
    title:
      "Why Fast Bridges Increase Systemic Risk (And How to Set Sane User Expectations)",
    description:
      "Understanding why fast bridges often increase systemic risk in Web3. Learn about finality trade-offs, optimistic assumptions, and how to set realistic user expectations for cross-chain transfers.",
    category: BlogCategory.MultichainOFT,
    tags: [
      "bridges",
      "systemic-risk",
      "finality",
      "ux",
      "risk-model",
      "user-expectations",
    ],
    publishedDate: "2025-09-28",
    lastUpdated: "2025-09-28",
    author: "Jeremy R DeYoung",
  },
  {
    route: ROUTES.BLOG_REPLAY_ATTACKS_REORG_RISK,
    title:
      "Replay Attacks, Reorg Risk, and Message Validation Failures in Cross-Chain Systems",
    description:
      "Understanding replay attacks, blockchain reorganizations, and message validation failures in cross-chain systems. Learn how nonces, timestamps, and transfer IDs prevent these attacks.",
    category: BlogCategory.Security,
    tags: [
      "replay-attacks",
      "reorg",
      "message-validation",
      "nonces",
      "timestamps",
      "cross-chain",
    ],
    publishedDate: "2025-09-26",
    lastUpdated: "2025-09-26",
    author: "Jeremy R DeYoung",
  },
  {
    route: ROUTES.BLOG_LEGAL_COMPLIANCE_FRAMEWORKS_ANONYMOUS_WEB3,
    title: "Legal Compliance Frameworks for Anonymous Web3 Participants",
    description:
      "Understanding how regulatory frameworks can accommodate anonymous participants through eligibility verification rather than identity disclosure. Learn how KYC/AML and sanctions screening work with pseudonymous systems.",
    category: BlogCategory.Compliance,
    tags: [
      "legal-compliance",
      "regulatory-frameworks",
      "anonymous",
      "web3",
      "kyc",
      "aml",
      "sanctions",
    ],
    publishedDate: "2025-09-24",
    lastUpdated: "2025-09-24",
    author: "Jeremy R DeYoung",
  },
  {
    route: ROUTES.BLOG_CAIP_STANDARDS_CROSS_CHAIN_IDENTITY,
    title:
      "CAIP Standards for Cross-Chain Identity: Implementing Chain Agnostic Improvement Proposals",
    description:
      "Understanding how CAIP standards enable true cross-chain identity by standardizing chain IDs and account identifiers across different blockchain ecosystems. Learn about CAIP-2 and CAIP-10 implementation.",
    category: BlogCategory.Identity,
    tags: [
      "caip",
      "cross-chain-identity",
      "standards",
      "caip-2",
      "caip-10",
      "chain-agnostic",
      "web3",
    ],
    publishedDate: "2025-09-22",
    lastUpdated: "2025-09-22",
    author: "Jeremy R DeYoung",
  },
  {
    route: ROUTES.BLOG_RUG_PULL_DETECTION,
    title: "Rug Pull Detection: What Platforms Can Detect vs What Users Must Verify",
    description:
      "Understanding what platforms can detect in rug pull detection versus what users must verify themselves. Learn about structural risks, compliance checks, and user-side due diligence.",
    category: BlogCategory.LaunchIntegrity,
    tags: [
      "rug-pulls",
      "detection",
      "due-diligence",
      "platform-controls",
      "user-verification",
    ],
    publishedDate: "2025-09-20",
    lastUpdated: "2025-09-20",
    author: "Jeremy R DeYoung",
  },
  {
    route: ROUTES.BLOG_REPUTATION_PORTABILITY_ACROSS_CHAINS,
    title:
      "Reputation Portability Across Chains: Maintaining Anonymity While Building Trust",
    description:
      "Understanding how reputation can be aggregated and verified across multiple chains without linking to real-world identity. Learn how cross-chain reputation systems enable trust portability while preserving privacy.",
    category: BlogCategory.Identity,
    tags: [
      "reputation",
      "cross-chain",
      "portability",
      "anonymity",
      "trust",
      "oft",
      "layerzero",
    ],
    publishedDate: "2025-09-18",
    lastUpdated: "2025-09-18",
    author: "Jeremy R DeYoung",
  },
  {
    route: ROUTES.BLOG_NON_CUSTODIAL_MARKETPLACE_ARCHITECTURE,
    title:
      "Non-Custodial Marketplace Architecture: Enabling Trustless Trading Without Custody",
    description:
      "Understanding how non-custodial marketplace architecture ensures users maintain full control over their assets and private keys, with the platform providing infrastructure without custody.",
    category: BlogCategory.Architecture,
    tags: [
      "non-custodial",
      "marketplace",
      "architecture",
      "trustless-trading",
      "user-sovereignty",
      "escrow",
    ],
    publishedDate: "2025-09-16",
    lastUpdated: "2025-09-16",
    author: "Jeremy R DeYoung",
  },
  {
    route: ROUTES.BLOG_MEV_FRONT_RUNNING,
    title: "What Is MEV/Front-Running and Why It Matters Even If You're Not a Trader",
    description:
      "Understanding MEV and front-running in blockchain systems. Learn how transaction ordering affects users, practical mitigations, and platform-side protections.",
    category: BlogCategory.Security,
    tags: ["mev", "front-running", "transaction-ordering", "user-protection", "mempool"],
    publishedDate: "2025-09-14",
    lastUpdated: "2025-09-14",
    author: "Jeremy R DeYoung",
  },
  {
    route: ROUTES.BLOG_CRYPTO_REGULATION_TRENDS_2026,
    title: "Crypto Regulation Trends: What Changes in 2026 Will Affect Launch Platforms",
    description:
      "Understanding crypto regulation trends in 2026 and their impact on launch platforms. Learn about US, EU, and Singapore regulatory changes affecting token launches, marketing, and disclosures.",
    category: BlogCategory.Compliance,
    tags: [
      "regulation",
      "2026-trends",
      "compliance",
      "sec",
      "mica",
      "mas",
      "launch-platforms",
    ],
    publishedDate: "2025-09-12",
    lastUpdated: "2025-09-12",
    author: "Jeremy R DeYoung",
  },
  {
    route: ROUTES.BLOG_USER_PRIVACY_COMPLIANCE,
    title:
      "User Privacy vs Compliance: Designing Systems That Minimize Data While Meeting Obligations",
    description:
      "Understanding how to design systems that balance user privacy with compliance obligations. Learn about data minimization patterns, auditability without oversharing, and transparent privacy-preserving compliance.",
    category: BlogCategory.Compliance,
    tags: [
      "privacy",
      "compliance",
      "data-minimization",
      "gdpr",
      "transparency",
      "audit-trails",
      "encryption",
    ],
    publishedDate: "2025-09-10",
    lastUpdated: "2025-09-10",
    author: "Jeremy R DeYoung",
  },
  {
    route: ROUTES.BLOG_ZERO_KNOWLEDGE_PROOFS_REGULATORY_COMPLIANCE,
    title:
      "Zero-Knowledge Proofs for Regulatory Compliance: Proving Eligibility Without Disclosure",
    description:
      "Understanding how zero-knowledge proofs enable platforms to prove regulatory compliance without revealing personal information. Learn the technical foundations of ZK proofs for compliance use cases.",
    category: BlogCategory.Privacy,
    tags: [
      "zero-knowledge",
      "zk-proofs",
      "regulatory-compliance",
      "privacy",
      "compliance",
      "kyc",
      "aml",
    ],
    publishedDate: "2025-09-08",
    lastUpdated: "2025-09-08",
    author: "Jeremy R DeYoung",
  },
  {
    route: ROUTES.BLOG_SECURITY_METRICS,
    title:
      "Security Metrics We Think Every Platform Should Publish (And How to Read Them)",
    description:
      "Understanding which security metrics platforms should publish publicly and how to read them. Learn about incident rates, response times, auth failures, policy enforcement, and how to avoid misleading vanity metrics.",
    category: BlogCategory.Security,
    tags: [
      "security-metrics",
      "transparency",
      "incident-rate",
      "response-time",
      "auth-failures",
      "policy-enforcement",
      "trust-signals",
    ],
    publishedDate: "2025-09-06",
    lastUpdated: "2025-09-06",
    author: "Jeremy R DeYoung",
  },
  {
    route: ROUTES.BLOG_ON_CHAIN_MONITORING,
    title: "On-Chain Monitoring 101: What It Can Catch Early (And What It Can't)",
    description:
      "Understanding what on-chain monitoring can catch early and what it can't. Learn about config drift, peer mismatches, fee misconfigurations, unusual inflight growth, and the limitations of on-chain monitoring.",
    category: BlogCategory.Security,
    tags: [
      "on-chain-monitoring",
      "blockchain-monitoring",
      "config-drift",
      "inflight-tracking",
      "cross-chain",
      "detection",
      "anomaly-detection",
    ],
    publishedDate: "2025-09-04",
    lastUpdated: "2025-09-04",
    author: "Jeremy R DeYoung",
  },
  {
    route: ROUTES.BLOG_EMERGENCY_WITHDRAWALS_CIRCUIT_BREAKERS,
    title:
      "Emergency Withdrawals and Circuit Breakers: When Pausing Is a Feature, Not a Failure",
    description:
      "Understanding emergency withdrawals and circuit breakers in blockchain systems. Learn when pausing is a user-protection mechanism, how to pause safely, and how to resume operations without compromising security.",
    category: BlogCategory.Operations,
    tags: [
      "emergency-withdrawals",
      "circuit-breakers",
      "pause-mechanisms",
      "incident-response",
      "recovery",
      "user-protection",
      "auditability",
    ],
    publishedDate: "2025-09-02",
    lastUpdated: "2025-09-02",
    author: "Jeremy R DeYoung",
  },
  {
    route: ROUTES.BLOG_ANONYMOUS_STAKING_AND_GOVERNANCE,
    title: "Anonymous Staking and Governance: Participating Without Identity Disclosure",
    description:
      "Understanding how staking and governance participation can work effectively with pseudonymous participants using reputation-based influence. Learn how anonymous participation prevents capture while maintaining accountability.",
    category: BlogCategory.Governance,
    tags: [
      "staking",
      "governance",
      "anonymity",
      "reputation",
      "voting-power",
      "sybil-resistance",
    ],
    publishedDate: "2025-08-31",
    lastUpdated: "2025-08-31",
    author: "Jeremy R DeYoung",
  },
  {
    route: ROUTES.BLOG_STAKING_AND_SECURITY,
    title:
      "Staking and Security: How Staking Can Improve Security, and How It Can Go Wrong",
    description:
      "Understanding how staking can align incentives and strengthen governance while introducing contract, custody, and centralization risks. Learn how to evaluate staking models and avoid security theater.",
    category: BlogCategory.Tokenomics,
    tags: [
      "staking",
      "security",
      "governance",
      "tokenomics",
      "user-protection",
      "risk-model",
      "slashing",
    ],
    publishedDate: "2025-08-29",
    lastUpdated: "2025-08-29",
    author: "Jeremy R DeYoung",
  },
  {
    route: ROUTES.BLOG_AI_BLOCKCHAIN_SECURITY,
    title:
      "AI + Blockchain Security: Threat, Tool, or Both? (Fraud Detection, Abuse, and Monitoring)",
    description:
      "Understanding AI and machine learning in blockchain security. Learn how AI can be both a threat (adversarial ML, prompt injection) and a tool (fraud detection, anomaly detection) for blockchain security.",
    category: BlogCategory.Security,
    tags: [
      "ai",
      "machine-learning",
      "fraud-detection",
      "security",
      "monitoring",
      "threat-detection",
      "abuse-prevention",
    ],
    publishedDate: "2025-08-27",
    lastUpdated: "2025-08-27",
    author: "Jeremy R DeYoung",
  },
  {
    route: ROUTES.BLOG_BLOCKCHAIN_ANALYTICS,
    title:
      "Blockchain Analytics for Investor Protection: Scam Patterns, Wallet Clustering, and Limitations",
    description:
      "Understanding blockchain analytics for investor protection. Learn what blockchain analytics can reveal about scam patterns, wallet clustering, and transaction analysis, and understand their limitations.",
    category: BlogCategory.Security,
    tags: [
      "blockchain-analytics",
      "investor-protection",
      "scam-detection",
      "wallet-clustering",
      "risk-analysis",
      "education",
    ],
    publishedDate: "2025-08-25",
    lastUpdated: "2025-08-25",
    author: "Jeremy R DeYoung",
  },
  {
    route: ROUTES.BLOG_CHOOSE_TRUSTED_LAUNCHPAD,
    title: "How to Choose a Trusted Launchpad: An Institutional + Retail Checklist",
    description:
      "Learn how to choose a trusted launchpad with checklists for both institutional and retail investors. Understand what to look for in security, compliance, transparency, and platform controls.",
    category: BlogCategory.LaunchIntegrity,
    tags: [
      "launchpads",
      "due-diligence",
      "investor-protection",
      "security-checklist",
      "compliance",
      "trust-signals",
    ],
    publishedDate: "2025-08-23",
    lastUpdated: "2025-08-23",
    author: "Jeremy R DeYoung",
  },
  {
    route: ROUTES.BLOG_BUG_BOUNTIES,
    title:
      "Bug Bounties and Responsible Disclosure: How Security Research Improves Resilience",
    description:
      "Understanding bug bounties and responsible disclosure in blockchain security. Learn how security research improves resilience, what responsible disclosure means, and best practices for researchers and platforms.",
    category: BlogCategory.Security,
    tags: [
      "bug-bounties",
      "responsible-disclosure",
      "security-research",
      "vulnerability-disclosure",
      "security-culture",
    ],
    publishedDate: "2025-08-21",
    lastUpdated: "2025-08-21",
    author: "Jeremy R DeYoung",
  },
  {
    route: ROUTES.BLOG_SECURITY_CULTURE,
    title:
      "Security Culture as a Product Feature: Transparency, Education, and Response Time",
    description:
      "Understanding security culture as a product feature. Learn how transparency, education, and response time differentiate platforms and build trust through security culture.",
    category: BlogCategory.Security,
    tags: [
      "security-culture",
      "transparency",
      "education",
      "response-time",
      "accountability",
      "trust",
    ],
    publishedDate: "2025-08-19",
    lastUpdated: "2025-08-19",
    author: "Jeremy R DeYoung",
  },
];

const BLOG_POSTS_BY_ROUTE: Record<string, BlogPostMeta> = BLOG_POSTS.reduce(
  (acc, post) => {
    acc[post.route] = post;
    return acc;
  },
  {} as Record<string, BlogPostMeta>
);

export function getBlogPostByRoute(route: string): BlogPostMeta | undefined {
  return BLOG_POSTS_BY_ROUTE[route];
}

function scoreRelatedPost(args: {
  current: BlogPostMeta;
  candidate: BlogPostMeta;
  preferredTags?: string[];
}): number {
  const { current, candidate, preferredTags } = args;

  if (candidate.route === current.route) return -1;

  const currentTags = new Set(current.tags);
  const preferredTagSet = new Set(preferredTags ?? []);

  let score = 0;

  if (candidate.category === current.category) score += 3;

  for (const tag of candidate.tags) {
    if (currentTags.has(tag)) score += 2;
    if (preferredTagSet.has(tag)) score += 1;
  }

  return score;
}

export function getRelatedBlogPosts(args: {
  currentRoute: string;
  limit?: number;
  preferredTags?: string[];
}): BlogPostMeta[] {
  const { currentRoute, limit = 4, preferredTags } = args;
  const current = getBlogPostByRoute(currentRoute);
  if (!current) return [];

  return BLOG_POSTS.slice()
    .filter((candidate) => isPostPublished(candidate)) // Only include published posts
    .map((candidate) => ({
      candidate,
      score: scoreRelatedPost({ current, candidate, preferredTags }),
    }))
    .filter((x) => x.score > 0)
    .sort((a, b) => b.score - a.score)
    .slice(0, limit)
    .map((x) => x.candidate);
}
